import { TYPES } from '../../../render/constants';
import { radiusToRounded, round } from './html';
import { deserializeDashArray, deserializePoints } from '../../../render/line';
import { getNodeClip, serializeClip } from '../../../render/utils/image-clip';
import {
  isGradientPatternFill,
  parseGradientPatternFill,
} from '../../../render/utils/gradient-pattern';

const PRESET_CLASSES = {
  textBase: 'sd-t',
  imageBase: 'sd-i',
  shapeBase: 'sd-s',
  pathBase: 'sd-p',
  lineBase: 'sd-l',
  frameBase: 'sd-f',
  groupBase: 'sd-g',
  gradientText: 'sd-gt',
};

const PRESET_STYLES = `<style>
.sd-t{margin:0;box-sizing:border-box;white-space:pre-wrap;word-break:normal;overflow-wrap:break-word;line-break:strict;text-rendering:geometricPrecision}
.sd-i,.sd-s,.sd-p,.sd-l,.sd-f,.sd-g{display:block}
.sd-gt{color:transparent;background-clip:text;-webkit-background-clip:text;background-size:100% 100%;background-repeat:no-repeat;-webkit-text-fill-color:transparent}
</style>`;

export async function node2HTML(
  store,
  nodes,
  {
    useAssets = false,
    useHTML = false,
    useSrc = false,
    useRoot = true,
    useId = false,
    useMeta = false,
    forLLM = false,
  } = {}
) {
  if (!Array.isArray(nodes)) {
    nodes = [nodes];
  }
  const rect = store.api.getNodesRegion(nodes);
  const assets = useAssets ? {} : null;
  const result = node2HTMLServer(store, nodes, {
    rect,
    assets,
    useAssets,
    useHTML,
    useRoot,
    useSrc,
    useId,
    useMeta,
    forLLM,
  });
  return result;
}

function kebabCase(str) {
  return str.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
}

function escapeAttr(value) {
  if (value === undefined || value === null) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function styleObjectToString(style) {
  return Object.entries(style)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${kebabCase(k)}:${v}`)
    .join(';');
}

/**
 * Convert gradient-pattern fill to CSS background properties
 * @param {string} fill - The fill value (may be gradient-pattern or regular fill)
 * @returns {Object} - CSS style object with background properties
 */
function fillToBackgroundStyle(fill) {
  if (!fill || typeof fill !== 'string') {
    return { background: fill || 'none' };
  }

  // Check if it's a gradient-pattern
  if (isGradientPatternFill(fill)) {
    const patternInfo = parseGradientPatternFill(fill);
    if (patternInfo) {
      const { gradient, patternWidth, patternHeight, widthUnit, heightUnit } = patternInfo;
      return {
        backgroundImage: gradient,
        backgroundSize: `${patternWidth}${widthUnit} ${patternHeight}${heightUnit}`,
        backgroundRepeat: 'repeat',
      };
    }
  }

  return { background: fill };
}

function node2HTMLServer(
  store,
  nodes,
  { rect, assets, useAssets, useHTML, useRoot, useSrc, useId, useMeta, forLLM }
) {
  // Use sequential short keys for better LLM recall instead of random UUIDs
  let assetCounter = 0;
  const createAsset = (src) => {
    assetCounter++;
    const key = `asset://img_${assetCounter}`;
    if (assets) assets[key] = src;
    return key;
  };

  const node = nodes.length === 1 ? nodes[0] : null;
  const isSingleFrame = node?.type === TYPES.frame;

  const seedeRootClasses = `seede-root relative w-[${round(rect.w)}px] h-[${round(rect.h)}px]`;
  const seedeRootAttrs = [];
  seedeRootAttrs.push(`data-width="${round(rect.w)}"`);
  seedeRootAttrs.push(`data-height="${round(rect.h)}"`);
  if (forLLM) {
    seedeRootAttrs.push(`data-seede-mode="llm"`);
  }
  if (isSingleFrame) {
    if (node.name) seedeRootAttrs.push(`data-name="${escapeAttr(node.name)}"`);
    if (node.description) seedeRootAttrs.push(`data-description="${escapeAttr(node.description)}"`);
  }
  if (isSingleFrame && node.fill) {
    seedeRootAttrs.push(`style="background:${escapeAttr(node.fill)}"`);
    seedeRootAttrs.push(`data-fill="${escapeAttr(node.fill)}"`);
  }

  // Expand nodes for single frame
  let renderNodes = nodes;
  if (isSingleFrame) {
    renderNodes = store.api.getChildren(node.id) || [];
  }

  const childHTML = renderNodes
    .map((n) => {
      const box = store.api.getNodeBox(n);
      const newNode = {
        ...n,
        x: box.x - rect.x,
        y: box.y - rect.y,
      };
      return serializeNode(newNode, store, {
        createAsset: useAssets ? createAsset : null,
        useSrc,
        useId,
        useMeta,
      });
    })
    .join('');

  let bodyHTML = `<div id="root"><div class="${seedeRootClasses}" ${seedeRootAttrs.join(
    ' '
  )}>${childHTML}</div></div>`;

  if (!useRoot) {
    const innerClass = `relative w-[${round(rect.w)}px] h-[${round(rect.h)}px]`;
    const attrs = [
      `class="${innerClass}"`,
      `data-width="${round(rect.w)}"`,
      `data-height="${round(rect.h)}"`,
    ];
    if (forLLM) {
      attrs.push(`data-seede-mode="llm"`);
    }
    bodyHTML = `<div ${attrs.join(' ')}>${childHTML}</div>`;
  }

  if (useHTML) {
    let html = `<!doctype html>
<html>
<head>
  <meta charset="UTF-8" />
  <meta http-equiv="Content-Type" content="text/html; charset=UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <script src="https://cdn.tailwindcss.com"></script>
  ${PRESET_STYLES}
</head>
<body class="relative" style="width:${round(rect.w)}px;height:${round(rect.h)}px;">
  ${bodyHTML}
</body>
</html>`;
    html = injectFontStyles(html, store.fonts);
    if (assets) return { assets, html };
    return html;
  }

  if (assets) return { assets, html: bodyHTML };
  return bodyHTML;
}

function serializeNode(node, store, options = {}) {
  const { useId = false, useMeta = false } = options;
  const nodeBox = node;
  const positionClasses = `absolute left-[${round(nodeBox.x)}px] top-[${round(nodeBox.y)}px]`;
  const sizeClasses = `w-[${round(node.w)}px] h-[${round(node.h)}px]`;
  const effectiveRadius = getEffectiveRadius(node);
  const roundedClasses = radiusToRounded(effectiveRadius);
  const opacity = node.opacity ?? 1;
  const opacityClasses = opacity !== 1 ? `opacity-[${node.opacity}]` : '';
  let strokeClasses = '';
  let strokeStyle = {};
  if (node.stroke && node.strokeWidth) {
    const borderStyleClass = node.strokeDasharray ? 'border-dashed' : '';
    strokeClasses =
      `border border-[${node.strokeWidth}] border-[${node.stroke}] ${borderStyleClass}`.trim();
  }

  const style = {};
  if (node.shadow) style.boxShadow = node.shadow;
  if (node.filter) style.filter = node.filter;
  if (node.clipPath) style.clipPath = node.clipPath;
  if (node.transform) style.transform = node.transform;

  const commonAttrs = [];
  if (useId) {
    commonAttrs.push(`id="${escapeAttr(node.id)}"`);
    commonAttrs.push(`data-type="${escapeAttr(node.type)}"`);
  }
  if (useMeta) {
    if (node.id) commonAttrs.push(`data-node-id="${escapeAttr(node.id)}"`);
    if (node.type) commonAttrs.push(`data-node-type="${escapeAttr(node.type)}"`);
    if (node.name) commonAttrs.push(`data-node-name="${escapeAttr(node.name)}"`);
    if (node.description)
      commonAttrs.push(`data-node-description="${escapeAttr(node.description)}"`);
    if (typeof nodeBox.x === 'number') commonAttrs.push(`data-x="${round(nodeBox.x)}"`);
    if (typeof nodeBox.y === 'number') commonAttrs.push(`data-y="${round(nodeBox.y)}"`);
    if (typeof node.w === 'number') commonAttrs.push(`data-w="${round(node.w)}"`);
    if (typeof node.h === 'number') commonAttrs.push(`data-h="${round(node.h)}"`);
  }

  const classAttr = mergeClasses([
    positionClasses,
    sizeClasses,
    strokeClasses,
    roundedClasses,
    opacityClasses,
  ]);
  const styleAttr = styleObjectToString(style);

  const context = {
    commonAttrs,
    positionClasses,
    sizeClasses,
    strokeClasses,
    roundedClasses,
    opacityClasses,
    style,
    classAttr,
    styleAttr,
  };

  const renderer = renderers[node.type] || renderers[TYPES.group];
  return renderer(node, store, options, context);
}

/**
 * Resolve an effective radius value for HTML export.
 *
 * Priority:
 * 1. Explicit `node.radius` (legacy / canvas path, supports CSS-like strings)
 *
 * The return value is formatted so that `radiusToRounded` can consume it
 * (number or CSS border-radius string such as "10px" or "10px 0 10px 0").
 */
function getEffectiveRadius(node) {
  if (node.radius && node.radius !== '0px' && node.radius !== 0) {
    return node.radius;
  }

  return '';
}

const renderers = {
  [TYPES.text]: renderText,
  [TYPES.image]: renderImage,
  [TYPES.shape]: renderShape,
  [TYPES.path]: renderPath,
  [TYPES.line]: renderLine,
  [TYPES.frame]: renderFrame,
  [TYPES.group]: renderGroup,
};

function renderText(node, store, options, context) {
  const { commonAttrs, classAttr, style } = context;
  const textStyles = {};
  const isGradientText = node.fill && String(node.fill).includes('gradient');
  let gradientTextClass = '';

  if (isGradientText) {
    gradientTextClass = PRESET_CLASSES.gradientText;
    textStyles.backgroundImage = node.fill;
  } else if (node.fill) {
    textStyles.color = node.fill;
  }
  if (node.shadow && node.shadow !== 'none') {
    textStyles.textShadow = node.shadow;
  }
  if (node.background) {
    textStyles.background = node.background;
  }
  if (node.padding) textStyles.padding = node.padding;
  if (node.fontFamily) textStyles.fontFamily = node.fontFamily;
  if (node.fontSize) textStyles.fontSize = `${node.fontSize}px`;
  if (node.fontWeight && node.fontWeight !== 'normal' && node.fontWeight !== '400')
    textStyles.fontWeight = node.fontWeight;
  if (node.fontStyle && node.fontStyle !== 'normal') textStyles.fontStyle = node.fontStyle;
  if (node.lineHeight)
    textStyles.lineHeight =
      typeof node.lineHeight === 'number' ? Number(node.lineHeight.toFixed(2)) : node.lineHeight;
  if (node.textAlign) textStyles.textAlign = node.textAlign;
  if (node.alignItems) textStyles.alignItems = node.alignItems;
  if (node.letterSpacing) textStyles.letterSpacing = `${node.letterSpacing}px`;
  if (node.textDecoration || node.decoration) {
    const decoration = node.textDecoration || node.decoration;
    if (decoration === 'underline') textStyles.textDecoration = 'underline';
    else if (decoration === 'lineThrough' || decoration === 'line-through')
      textStyles.textDecoration = 'line-through';
  }
  if (node.strokeWidth && node.stroke) {
    textStyles.webkitTextStroke = `${node.strokeWidth}px ${node.stroke}`;
    textStyles.paintOrder = 'stroke fill';
  }

  const attrs = [...commonAttrs];
  const fullClassName = mergeClasses([PRESET_CLASSES.textBase, gradientTextClass, classAttr]);
  if (fullClassName) attrs.push(`class="${escapeAttr(fullClassName)}"`);
  const styleNoBoxShadow = { ...style };
  delete styleNoBoxShadow.boxShadow;
  const mergedStyle = styleObjectToString({ ...styleNoBoxShadow, ...textStyles });
  if (mergedStyle) attrs.push(`style="${escapeAttr(mergedStyle)}"`);
  if (node.fillMode) attrs.push(`data-fill-mode="${escapeAttr(node.fillMode)}"`);
  if (node.shadow && node.shadow !== 'none')
    attrs.push(`data-text-shadow="${escapeAttr(node.shadow)}"`);
  if (node.styles && node.styles.length > 0)
    attrs.push(`data-styles='${escapeAttr(JSON.stringify(node.styles))}'`);
  if (node.highlights && node.highlights.length > 0)
    attrs.push(`data-highlights='${escapeAttr(JSON.stringify(node.highlights))}'`);
  if (node.effect) attrs.push(`data-effect='${escapeAttr(JSON.stringify(node.effect))}'`);
  if (node.alignItems) attrs.push(`data-align-items='${escapeAttr(node.alignItems)}'`);

  const textContent = (node.text || '').replace(/\n/g, '<br>');
  return `<p ${attrs.join(' ')}>${textContent}</p>`;
}

function renderImage(node, store, options, context) {
  const { createAsset, useSrc } = options;
  const {
    commonAttrs,
    positionClasses,
    sizeClasses,
    strokeClasses,
    roundedClasses,
    opacityClasses,
    styleAttr,
  } = context;

  let srcAttr = '';
  const src = node.src;
  if (createAsset) {
    if (src) srcAttr = createAsset(src);
  } else if (useSrc) {
    if (src) {
      if (src.startsWith('asset://')) {
        const id = src.replace('asset://', '');
        srcAttr = `https://assets.seede.ai/asset/${id}`;
      } else {
        srcAttr = src;
      }
    }
  } else {
    if (src && !src.startsWith('data:')) srcAttr = src;
  }

  const objectFitClasses = `object-${node.fit || 'fill'}`;
  const fullClassName = mergeClasses([
    PRESET_CLASSES.imageBase,
    positionClasses,
    sizeClasses,
    strokeClasses,
    roundedClasses,
    opacityClasses,
    objectFitClasses,
  ]);
  const attrs = [...commonAttrs];
  if (fullClassName) attrs.push(`class="${escapeAttr(fullClassName)}"`);
  if (styleAttr) attrs.push(`style="${escapeAttr(styleAttr)}"`);
  if (srcAttr) attrs.push(`src="${escapeAttr(srcAttr)}"`);
  if (node.alt) {
    attrs.push(`alt="${escapeAttr(node.alt)}"`);
  } else if (node.prompt) {
    attrs.push(`alt="${escapeAttr(node.prompt)}"`);
  }
  if (node.prompt) attrs.push(`data-origin-prompt="${escapeAttr(node.prompt)}"`);
  if (node.clip) {
    const clip = getNodeClip(node);
    if (clip && !(clip.x === 0 && clip.y === 0 && clip.w === 0 && clip.h === 0)) {
      attrs.push(`data-clip="${escapeAttr(serializeClip(clip))}"`);
    }
  }
  return `<img ${attrs.join(' ')} />`;
}

function renderShape(node, store, options, context) {
  const { commonAttrs, positionClasses, sizeClasses, roundedClasses, opacityClasses, style } =
    context;
  const attrs = [...commonAttrs];
  const fullClassName = mergeClasses([
    PRESET_CLASSES.shapeBase,
    positionClasses,
    sizeClasses,
    roundedClasses,
    opacityClasses,
  ]);
  if (fullClassName) attrs.push(`class="${escapeAttr(fullClassName)}"`);
  const localStyle = { ...style };
  // Handle gradient-pattern fills by converting to proper CSS
  const bgStyle = fillToBackgroundStyle(node.fill);
  Object.assign(localStyle, bgStyle);
  const strokeWidth = parseFloat(node.strokeWidth);
  if (node.stroke && strokeWidth) {
    const sw = isNaN(strokeWidth) ? 0 : strokeWidth;
    localStyle.border = `${sw}px solid ${node.stroke}`;
  }
  if (node.backdropFilter) {
    localStyle.backdropFilter = node.backdropFilter;
  }
  const merged = styleObjectToString(localStyle);
  if (merged) attrs.push(`style="${escapeAttr(merged)}"`);
  if (node.fill) attrs.push(`data-fill="${escapeAttr(node.fill)}"`);
  return `<div ${attrs.join(' ')}></div>`;
}

function renderPath(node, store, options, context) {
  const { commonAttrs, positionClasses, sizeClasses, opacityClasses } = context;
  const attrs = [`data-type="${TYPES.path}"`, ...commonAttrs];
  const fullClassName = mergeClasses([
    PRESET_CLASSES.pathBase,
    positionClasses,
    sizeClasses,
    opacityClasses,
  ]);
  if (fullClassName) attrs.push(`class="${escapeAttr(fullClassName)}"`);
  attrs.push(`viewBox="0 0 ${round(node.w)} ${round(node.h)}"`);
  attrs.push(`xmlns="http://www.w3.org/2000/svg"`);
  const pathD = escapeAttr(node.d || node.path || '');
  const fill = escapeAttr(node.fill || 'none');
  const stroke = node.stroke
    ? ` stroke="${escapeAttr(node.stroke)}" stroke-width="${escapeAttr(node.strokeWidth || 1)}"`
    : '';
  return `<svg ${attrs.join(' ')}><path d="${pathD}" fill="${fill}"${stroke}></path></svg>`;
}

function renderLine(node, store, options, context) {
  const { commonAttrs, positionClasses, sizeClasses, opacityClasses } = context;
  const markerSize = `data-marker-size="${node.markerSize || 16}"`;
  const fill = `data-fill="${node.fill || ''}"`;
  const attrs = [`data-type="${TYPES.line}"`, fill, markerSize, ...commonAttrs];
  const fullClassName = mergeClasses([
    PRESET_CLASSES.lineBase,
    positionClasses,
    sizeClasses,
    opacityClasses,
  ]);
  if (fullClassName) attrs.push(`class="${escapeAttr(fullClassName)}"`);
  attrs.push(`viewBox="0 0 ${round(node.w)} ${round(node.h)}"`);
  attrs.push(`xmlns="http://www.w3.org/2000/svg"`);

  // Ensure explicit size for environments without utility CSS
  attrs.push(`width="${round(node.w)}"`);
  attrs.push(`height="${round(node.h)}"`);

  const points = deserializePoints(node.points);
  const absPoints =
    points && points.length >= 2
      ? points.map((p) => `${p.x * node.w},${p.y * node.h}`).join(' ')
      : '';

  const attrsPolyline = [
    `points="${escapeAttr(absPoints)}"`,
    `fill="none"`,
    `stroke="${escapeAttr(node.fill || '#000000')}"`,
    `stroke-width="${escapeAttr(node.size || 1)}"`,
    `stroke-linecap="${escapeAttr(node.lineCap || 'butt')}"`,
    `stroke-linejoin="${escapeAttr(node.lineJoin || 'miter')}"`,
  ];
  if (node.startMarker && node.startMarker !== 'none')
    attrsPolyline.push(`marker-start="url(#arrowhead)"`);
  if (node.endMarker && node.endMarker !== 'none')
    attrsPolyline.push(`marker-end="url(#arrowhead)"`);
  if (node.strokeStyle === 'dashed' && node.dashArray) {
    const dashArray = deserializeDashArray(node.dashArray);
    if (dashArray.length > 0)
      attrsPolyline.push(`stroke-dasharray="${escapeAttr(dashArray.join(' '))}"`);
  }
  if (node.opacity !== undefined && node.opacity !== 1)
    attrsPolyline.push(`opacity="${escapeAttr(node.opacity)}"`);

  return `<svg ${attrs.join(' ')}><polyline ${attrsPolyline.join(' ')} /></svg>`;
}

function renderFrame(node, store, options, context) {
  const { commonAttrs, positionClasses, sizeClasses, roundedClasses, opacityClasses, style } =
    context;
  const attrs = [...commonAttrs];
  const fullClassName = mergeClasses([
    PRESET_CLASSES.frameBase,
    positionClasses,
    sizeClasses,
    roundedClasses,
    opacityClasses,
  ]);
  if (fullClassName) attrs.push(`class="${escapeAttr(fullClassName)}"`);
  const localStyle = { ...style };
  // Handle gradient-pattern fills by converting to proper CSS
  const bgStyle = fillToBackgroundStyle(node.fill);
  Object.assign(localStyle, bgStyle);
  const merged = styleObjectToString(localStyle);
  if (merged) attrs.push(`style="${escapeAttr(merged)}"`);
  const children = store.api.getChildren(node.id) || [];
  const html = children.map((child) => serializeNode(child, store, options)).join('');
  return `<div ${attrs.join(' ')}>${html}</div>`;
}

function renderGroup(node, store, options, context) {
  const { commonAttrs, positionClasses, sizeClasses, opacityClasses, roundedClasses, styleAttr } =
    context;
  const overflowClass = node.overflow === 'hidden' ? 'overflow-hidden' : '';
  const fullClassName = mergeClasses([
    PRESET_CLASSES.groupBase,
    positionClasses,
    sizeClasses,
    opacityClasses,
    roundedClasses,
    overflowClass,
  ]);
  const attrs = [...commonAttrs];
  if (fullClassName) attrs.push(`class="${escapeAttr(fullClassName)}"`);
  if (styleAttr) attrs.push(`style="${escapeAttr(styleAttr)}"`);
  const children = store.api.getChildren(node.id) || [];
  const html = children.map((child) => serializeNode(child, store, options)).join('');
  return `<div ${attrs.join(' ')}>${html}</div>`;
}

function mergeClasses(list) {
  return list.filter((n) => !!n).join(' ');
}

/**
 * @param {string} html
 * @param {import('../../../provider/font').Font[]} fontList
 * @returns
 */
export function injectFontStyles(html, fontList = []) {
  const matchedFonts = [];

  fontList.map((item) => {
    item.list.forEach((item) => {
      if (html.includes(item.name)) {
        matchedFonts.push(item);
      }
    });
  });
  // Inject matched fonts into HTML
  if (matchedFonts.length > 0) {
    let fontLinksTag = '';

    matchedFonts.forEach((font) => {
      if (font.url) {
        // For fonts with external URLs, add link tag
        fontLinksTag += `<link rel="stylesheet" href="${font.url}">\n`;
      } else if (font.content) {
        // For fonts with inline content, wrap in style tag
        fontLinksTag += `<style>\n${font.content}\n</style>\n`;
      }
    });

    // Insert font links at the beginning of the head section
    if (html.includes('<head>')) {
      html = html.replace('<head>', `<head>\n${fontLinksTag}`);
    } else if (html.includes('<html>')) {
      html = html.replace('<html>', `<html>\n<head>\n${fontLinksTag}</head>`);
    } else {
      // If no head or html tag, prepend to the entire HTML
      html = `${fontLinksTag}\n${html}`;
    }
  }

  return html;
}
